"use client";

import { useState } from "react";
import Link from "next/link";

interface FaqItem {
  question: string;
  answer: string;
}

interface Props {
  eyebrow: string;
  heading: string;
  body?: string;
  items: FaqItem[];
  /** Hide the "Talk to our team" link in the intro column. */
  hideContactLink?: boolean;
}

// Matches the marketing site's FAQ style (src/components/sections/products/shared/faq):
// a two-column section — intro on the left, an accordion of rounded cards on the right with a
// rotating chevron and a smooth grid-rows expand. Used by both tool content renderers
// (ToolContent + SliceZone) so the tool FAQs read identically to the rest of the site.
export default function FaqAccordion({
  eyebrow,
  heading,
  body,
  items,
  hideContactLink = false,
}: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr] lg:gap-16">
        <div>
          <p className="text-brand mb-3 text-sm font-medium">{eyebrow}</p>
          <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            {heading}
          </h2>
          {body && <p className="text-muted-foreground mb-6 leading-relaxed">{body}</p>}
          {!hideContactLink && (
            // Same button treatment as the marketing FAQ's "button" contact variant
            // (src/components/sections/products/shared/faq).
            <Link
              href="/contact"
              className="bg-primary text-primary-foreground inline-flex items-center rounded-lg px-5 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            >
              Talk to our team
            </Link>
          )}
        </div>

        <div className="space-y-3">
          {items.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={faq.question}
                className="border-border bg-card overflow-hidden rounded-xl border"
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="text-foreground flex w-full cursor-pointer items-center justify-between px-5 py-4 text-left text-sm font-semibold select-none"
                >
                  {faq.question}
                  <span
                    className={`text-muted-foreground ml-4 shrink-0 transition-transform duration-300 ease-out ${isOpen ? "rotate-180" : ""}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                >
                  <div className="overflow-hidden">
                    <div className="text-muted-foreground px-5 pt-1 pr-10 pb-5 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
